import React, { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import styles from './MultiFileUploadComponent.module.scss';
import ModalDialogComponent from '../ModalDialogComponent/ModalDialogComponent';
import FileUploadProgressComponent from '../FileUploadProgressComponent/FileUploadProgressComponent';

export interface FileToUpload {
    fileName: string;
    url?: string;
    file: File;
    progress?: number;
}

interface MultiFileUploadComponentProps {
    uploads: FileToUpload[];
    completed: (success: boolean) => void;
}

const MultiFileUploadComponent: FC<MultiFileUploadComponentProps> = (props) => {
    const { uploads, completed } = props;

    const [fileUploads, setFileUploads] = useState(uploads);
    const [aborting, setAborting] = useState(false);
    const abortingStateRef = React.useRef(aborting);
    const uploadFile = async (fileToUpload: FileToUpload) => {
        if (!fileToUpload.url || abortingStateRef.current) {
            return;
        }
        fileToUpload.progress = 0;

        const xhr = new XMLHttpRequest();
        await new Promise((resolve) => {
            xhr.upload.addEventListener('progress', (event) => {
                const abortingState = abortingStateRef.current;
                if (abortingState) {
                    xhr.abort();
                }
                if (event.lengthComputable) {
                    const progress = (event.loaded / event.total) * 100;
                    fileToUpload.progress = +progress.toFixed(0);
                    setFileUploads([...fileUploads]);
                }
            });
            xhr.addEventListener('loadend', () => {
                fileToUpload.progress = 100;
                setFileUploads([...fileUploads]);
                resolve(xhr.readyState === 4 && xhr.status === 200);
            });

            xhr.open('PUT', fileToUpload.url!, true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.send(fileToUpload.file);
        });
    };

    const complete = (v: boolean) => {
        if (!v) {
            setAborting(true);
            abortingStateRef.current = true;
            completed(false);
        } else {
            completed(true);
        }
    };

    const uploadFiles = async () => {
        for (let f = 0; f < fileUploads.length; f++) {
            const fileUpload = fileUploads[f];
            // eslint-disable-next-line no-await-in-loop
            await uploadFile(fileUpload);
        }
        if (!abortingStateRef.current) {
            complete(true);
        }
    };

    useEffect(() => {
        setTimeout(() => {
            uploadFiles();
        }, 1500);
    }, [uploads]);

    return (
        <div className={styles.MultiFileUploadComponent} data-testid="MultiFileUploadComponent">
            <ModalDialogComponent
                open
                title="Uploading"
                setOpen={(v) => complete(v)}
                confirmEnabled
                confirmText="Upload"
            >
                <Box sx={{ mb: 2 }}>
                    <Typography>Aborting upload will cancel all files</Typography>

                    {fileUploads.map((f) => (
                        <FileUploadProgressComponent
                            key={f.fileName}
                            filename={f.fileName}
                            value={f.progress ?? 0}
                        />
                    ))}
                </Box>
            </ModalDialogComponent>
        </div>
    );
};

export default MultiFileUploadComponent;
